import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { api, ApiError } from '../api/client'
import { useActiveIntegrator } from '../auth/active-integrator'

export function MerchantsPage() {
  const active = useActiveIntegrator()
  const { integratorId: paramId } = useParams()
  // `/integrators/:integratorId/merchants` wins (sys-admin drilled in);
  // bare `/merchants` picks whichever integrator the admin is currently
  // operating within (scoped admin's own, or sys-admin's override).
  const integratorId = paramId ?? active.integratorId
  const qc = useQueryClient()
  const [displayName, setDisplayName] = useState('')
  const [externalId, setExternalId] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['merchants', integratorId],
    queryFn: () => api.merchants.listForIntegrator(integratorId, 100, 0),
    enabled: Boolean(integratorId),
  })

  const create = useMutation({
    mutationFn: () => {
      const body: { displayName: string; externalId?: string } = { displayName: displayName.trim() }
      if (externalId.trim()) body.externalId = externalId.trim()
      return api.merchants.create(integratorId, body)
    },
    onSuccess: () => {
      setDisplayName('')
      setExternalId('')
      setCreateError(null)
      qc.invalidateQueries({ queryKey: ['merchants', integratorId] })
    },
    onError: (err) => {
      setCreateError(err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message)
    },
  })

  if (isLoading) return <div>Loading merchants…</div>
  if (error) return <div className="text-red-600">Failed to load: {(error as Error).message}</div>

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold">Merchants</h1>
        <Link to="/integrators" className="text-sm text-brand-700 hover:underline">
          ← back to integrators
        </Link>
      </div>
      <form
        className="mb-4 flex flex-wrap items-end gap-2 rounded border border-slate-200 bg-white p-3"
        onSubmit={(e) => {
          e.preventDefault()
          if (!displayName.trim()) return
          create.mutate()
        }}
      >
        <label className="text-xs text-slate-600">
          New merchant name
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className="mt-1 block w-64 rounded border border-slate-300 px-2 py-1 text-sm"
            placeholder="Acme Kft."
          />
        </label>
        <label className="text-xs text-slate-600">
          External ID (optional)
          <input
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            className="mt-1 block w-48 rounded border border-slate-300 px-2 py-1 text-sm"
            placeholder="CUST-42"
          />
        </label>
        <button
          type="submit"
          disabled={create.isPending || !displayName.trim()}
          className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40"
        >
          {create.isPending ? 'Creating…' : 'Create merchant'}
        </button>
        {createError && <span className="text-sm text-red-600">{createError}</span>}
      </form>

      <div className="overflow-hidden rounded border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-left text-xs font-semibold uppercase text-slate-600">
            <tr>
              <th className="px-3 py-2">Display name</th>
              <th className="px-3 py-2">External ID</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Connected at</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.items.map((m) => (
              <tr key={m.id} className="border-t border-slate-200">
                <td className="px-3 py-2">
                  <Link to={`/merchants/${m.id}`} className="font-medium text-brand-700 hover:underline">
                    {m.displayName}
                  </Link>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{m.externalId}</td>
                <td className="px-3 py-2">{m.status}</td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {new Date(m.connectedAt).toLocaleString()}
                </td>
                <td className="px-3 py-2 text-right text-xs">
                  <Link
                    to={`/merchants/${m.id}`}
                    className="text-brand-700 hover:underline"
                  >
                    manage →
                  </Link>
                  {' · '}
                  <Link
                    to={`/invoices?integratorId=${integratorId}&merchantId=${m.id}`}
                    className="text-brand-700 hover:underline"
                  >
                    invoices →
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.items.length === 0 && (
          <div className="p-6 text-center text-sm text-slate-500">No merchants connected yet.</div>
        )}
      </div>
    </div>
  )
}
